import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { storageFor } from 'ember-local-storage';

/**
 * Preferences Service
 *
 * Manages user preferences for column visibility, sorting, and display options.
 * Preferences are stored per-classgroup in localStorage.
 */
export default class PreferencesService extends Service {
  @storageFor('preferences') storage;

  // Tracked version to force reactivity when preferences change
  @tracked _version = 0;

  // Default column configuration
  // isDetailColumn: shown in secondary row on mobile, normal column on desktop
  // Timing columns (splits, finish): always in primary row, horizontal scroll on mobile
  static DEFAULT_COLUMNS = [
    // Primary columns (always in main row)
    { id: 'pos', name: 'Place', group: 'basic', defaultVisible: true, sortable: true, align: 'center', sticky: true, stickyIndex: 1, alwaysVisible: true },
    { id: 'bib', name: 'Bib', group: 'basic', defaultVisible: true, sortable: true, align: 'center', sticky: true, stickyIndex: 2 },
    { id: 'name', name: 'Name', group: 'basic', defaultVisible: true, sortable: true, align: 'start', sticky: true, stickyIndex: 3 },
    // Detail columns (secondary row on mobile, table columns on desktop)
    { id: 'club', name: 'Club', group: 'details', defaultVisible: true, sortable: true, align: 'start', isDetailColumn: true },
    { id: 'class', name: 'Class', group: 'details', defaultVisible: false, sortable: true, align: 'center', isDetailColumn: true },
    { id: 'country', name: 'Country', group: 'details', defaultVisible: false, sortable: true, align: 'start', isDetailColumn: true },
    { id: 'state', name: 'State', group: 'details', defaultVisible: false, sortable: true, align: 'start', isDetailColumn: true },
    { id: 'gender', name: 'Gender', group: 'details', defaultVisible: false, sortable: true, align: 'center', isDetailColumn: true },
    { id: 'town', name: 'Town', group: 'details', defaultVisible: false, sortable: true, align: 'start', isDetailColumn: true },
    // Splits are dynamic and added based on course (defaultVisible: true - shown by default)
    // Finish and time back columns
    { id: 'finish', name: 'Finish', group: 'timing', defaultVisible: true, sortable: true, align: 'center' },
    { id: 'timeBack', name: 'Back', group: 'timing', defaultVisible: true, sortable: true, align: 'center' },
    // Display options (not actual columns, just toggles)
    { id: 'showPredictions', name: 'Show Predicted Times', group: 'display', defaultVisible: false, isDisplayOption: true }
  ];

  /**
   * Get configuration for a specific classgroup
   * @param {string} classGroupUuid - The classgroup UUID
   * @param {Array} splitColumns - Optional array of split columns for default config
   */
  getClassGroupConfig(classGroupUuid, splitColumns = []) {
    // Access _version to create a tracked dependency
    // eslint-disable-next-line no-unused-vars
    const version = this._version;
    const configs = this.storage.get('classGroupConfigs') || {};
    return configs[classGroupUuid] || this.getDefaultConfig(splitColumns);
  }

  /**
   * Get default configuration
   * Splits ARE included by default - the overall classgroup view shows all
   * splits out of the box. Callers without split context (e.g., internal
   * updates) get the static defaults only.
   */
  getDefaultConfig(splitColumns = []) {
    const defaultVisible = this.getAvailableColumns(splitColumns)
      .filter(col => col.defaultVisible)
      .map(col => col.id);

    return {
      visibleColumns: [...defaultVisible],
      sortField: 'pos',
      sortDirection: 'asc',
      splitSortMode: 'elapsed' // 'elapsed' or 'split'
    };
  }

  /**
   * Update visible columns for a classgroup
   */
  updateVisibleColumns(classGroupUuid, visibleColumns) {
    const configs = this.storage.get('classGroupConfigs') || {};
    const config = configs[classGroupUuid] || this.getDefaultConfig();
    config.visibleColumns = visibleColumns;
    configs[classGroupUuid] = config;
    this.storage.set('classGroupConfigs', { ...configs });
  }

  /**
   * Check if a column is visible for a classgroup
   */
  isColumnVisible(classGroupUuid, columnId) {
    const config = this.getClassGroupConfig(classGroupUuid);
    return config.visibleColumns.includes(columnId);
  }

  /**
   * Toggle column visibility
   * @param {string} classGroupUuid - The classgroup UUID
   * @param {string} columnId - The column ID to toggle
   * @param {Array} currentVisibleColumns - Current visible column IDs (to preserve state)
   */
  toggleColumnVisibility(classGroupUuid, columnId, currentVisibleColumns = null) {
    // Use passed currentVisibleColumns if available, otherwise get from config
    const visibleColumns = currentVisibleColumns
      ? [...currentVisibleColumns]
      : [...this.getClassGroupConfig(classGroupUuid).visibleColumns];

    const index = visibleColumns.indexOf(columnId);

    if (index > -1) {
      visibleColumns.splice(index, 1);
    } else {
      visibleColumns.push(columnId);
    }

    this.updateVisibleColumns(classGroupUuid, visibleColumns);
    // Increment version to trigger reactivity
    this._version++;
  }

  /**
   * Update sort configuration for a classgroup
   */
  updateSort(classGroupUuid, sortField, sortDirection, splitSortMode = 'elapsed') {
    const configs = this.storage.get('classGroupConfigs') || {};
    const config = configs[classGroupUuid] || this.getDefaultConfig();
    config.sortField = sortField;
    config.sortDirection = sortDirection;
    config.splitSortMode = splitSortMode;
    configs[classGroupUuid] = config;
    this.storage.set('classGroupConfigs', { ...configs });
  }

  /**
   * Get sort configuration for a classgroup
   */
  getSortConfig(classGroupUuid) {
    const config = this.getClassGroupConfig(classGroupUuid);
    return {
      sortField: config.sortField || 'pos',
      sortDirection: config.sortDirection || 'asc',
      splitSortMode: config.splitSortMode || 'elapsed'
    };
  }

  /**
   * Get available columns definition (static + dynamic splits)
   * Split columns are NOT mobileHidden - they show in primary row with horizontal scroll
   */
  getAvailableColumns(splitColumns = []) {
    const columns = [...PreferencesService.DEFAULT_COLUMNS];

    // Find the index where finish column is (to insert splits before it)
    const finishIndex = columns.findIndex(col => col.id === 'finish');

    // Create split column definitions - NO mobileHidden, always in primary row
    const splitDefs = splitColumns.map(split => ({
      id: `split_${split.uuid}`,
      name: split.name,
      group: 'splits',
      defaultVisible: true, // Splits shown by default; toggle off via column selector
      sortable: true,
      align: 'center',
      isSplit: true,
      splitUuid: split.uuid
    }));

    // Insert splits before finish
    columns.splice(finishIndex, 0, ...splitDefs);

    return columns;
  }

  /**
   * Get columns grouped by category for the selector UI
   * Excludes columns marked as alwaysVisible since they can't be hidden
   */
  getColumnGroups(splitColumns = []) {
    const allColumns = this.getAvailableColumns(splitColumns)
      .filter(col => !col.alwaysVisible);

    return {
      basic: allColumns.filter(col => col.group === 'basic'),
      details: allColumns.filter(col => col.group === 'details'),
      splits: allColumns.filter(col => col.group === 'splits'),
      timing: allColumns.filter(col => col.group === 'timing'),
      display: allColumns.filter(col => col.group === 'display')
    };
  }

  /**
   * Reset configuration for a classgroup
   */
  resetClassGroupConfig(classGroupUuid) {
    const configs = this.storage.get('classGroupConfigs') || {};
    delete configs[classGroupUuid];
    this.storage.set('classGroupConfigs', { ...configs });
  }

  /**
   * Clear all stored preferences
   */
  clearAll() {
    this.storage.set('classGroupConfigs', {});
    this.storage.set('currentEventId', null);
  }

  // ============================================
  // Crossing Stats Visibility (for timing points)
  // ============================================

  static DEFAULT_CROSSING_STATS = ['place', 'elapsed', 'timeBack', 'gap'];

  /**
   * Get visible crossing stats configuration
   */
  getCrossingStatsConfig() {
    // Access _version for reactivity
    // eslint-disable-next-line no-unused-vars
    const version = this._version;
    return this.storage.get('visibleCrossingStats') || PreferencesService.DEFAULT_CROSSING_STATS;
  }

  /**
   * Toggle a crossing stat visibility
   */
  toggleCrossingStat(statId) {
    const stats = [...this.getCrossingStatsConfig()];
    const index = stats.indexOf(statId);

    if (index > -1) {
      stats.splice(index, 1);
    } else {
      stats.push(statId);
    }

    this.storage.set('visibleCrossingStats', stats);
    this._version++;
  }

  /**
   * Check if a crossing stat is visible
   */
  isCrossingStatVisible(statId) {
    return this.getCrossingStatsConfig().includes(statId);
  }

  /**
   * Get all available crossing stats for the selector
   */
  getAvailableCrossingStats() {
    return [
      { id: 'place', name: 'Place', alwaysVisible: true },
      { id: 'elapsed', name: 'Time', alwaysVisible: true },
      { id: 'timeBack', name: 'Leader', group: 'position' },
      { id: 'gap', name: 'Gap', group: 'position' },
      { id: 'timeOutTop10', name: 'Top 10', group: 'comparative' },
      { id: 'timeOutTop30', name: 'Top 30', group: 'comparative' },
      { id: 'timeGain10', name: 'Gain 10', group: 'comparative' },
      { id: 'classPlace', name: 'Class Place', group: 'class' },
      { id: 'classTimeBack', name: 'Class Time', group: 'class' }
    ];
  }
}
